import type { CogniteClient } from '@cognite/sdk';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { use } from 'react';

import { CogniteClientContext } from '#core/contexts/CogniteClientContext.ts';
import type { DataModelView } from '#core/lib/cognite/view.ts';

import { useDatasetResolver } from './useDatasetResolver.ts';
import type { DatasetResolver } from '#plugin-chat/lib/data-pipeline/resolver/dataset-resolver.ts';
import type { PipelineContext, PipelineStep } from '#plugin-chat/lib/data-pipeline/step/types.ts';
import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';
import type { ToolExecutionError } from '#plugin-chat/lib/tool/errors/index.ts';

/** Bundle returned by {@link PipelineContextBuilder}. */
export interface PipelineInvocation {
  /** Dataset resolver bound to this invocation's thread + query client. */
  resolve: DatasetResolver;
  /** Ready-to-pass {@link PipelineContext} for a pipeline step's `execute`. */
  context: PipelineContext;
  /**
   * Run a pipeline step against this invocation's context. Step-thrown
   * {@link ToolExecutionError} subclasses already carry their own structured
   * response and propagate unchanged to the tool layer.
   */
  runStep: <ParamsType>(step: PipelineStep<ParamsType>, params: ParamsType, input?: Dataset) => Promise<Dataset>;
}

/**
 * Builds a fresh pipeline context and chat-history store for one tool
 * invocation. Each call produces a new store so intermediate datasets from
 * a previous invocation do not leak into the next one.
 */
export type PipelineContextBuilder = (options?: { views?: readonly DataModelView[] }) => PipelineInvocation;

/** Dependencies accepted by {@link createPipelineInvocation}. */
export interface PipelineInvocationDeps {
  client: CogniteClient | undefined;
  queryClient: QueryClient;
  /** Dataset resolver bound to the current thread + query client. */
  resolve: DatasetResolver;
}

/**
 * Pure builder for a {@link PipelineInvocation} — no React involvement.
 *
 * Extracted from {@link usePipelineContext} so the resolver + context
 * wiring can be unit-tested without a React renderer and reused from
 * non-hook call sites. Step-thrown {@link ToolExecutionError} subclasses
 * already carry their own `toStructuredResponse()`, so they propagate
 * unchanged to the tool boundary.
 */
export function createPipelineInvocation(
  deps: PipelineInvocationDeps,
  options: { views?: readonly DataModelView[] } = {},
): PipelineInvocation {
  const context: PipelineContext = {
    resolve: deps.resolve,
    client: deps.client,
    queryClient: deps.queryClient,
    views: options.views,
  };
  return {
    resolve: deps.resolve,
    context,
    runStep: (step, params, input) => step.execute(params, input, context),
  };
}

/**
 * Hook bundling the dependencies a dataset-producing tool needs to run
 * pipeline steps: Cognite SDK client, TanStack query client, current
 * thread id, and chat-history accessors for name → (toolName, args)
 * lookup.
 *
 * Returns a factory rather than an already-built context so every tool
 * invocation gets a fresh {@link PipelineContext}. The dataset cache is
 * the `QueryClient` itself — there is no staging overlay. Chained
 * `sourceRef` resolutions within a streaming turn work because the
 * producer's execute wrapper populates the cache before the downstream
 * tool's execute runs; post-reload resolution works because the dataset
 * resolver reads `produceDataset` back from the assistant-ui model
 * context and invokes it lazily on the first cache-miss lookup.
 *
 * The Cognite client is read from context via `use()` and may be
 * `undefined` outside a provider; the query client is always present
 * (TanStack's hook throws otherwise). Tools that need a live CDF client
 * surface errors from their step layer when the dep is missing.
 */
export function usePipelineContext(): PipelineContextBuilder {
  const client = use(CogniteClientContext) ?? undefined;
  const queryClient = useQueryClient();
  const resolve = useDatasetResolver();

  return (options) => createPipelineInvocation({ client, queryClient, resolve }, options);
}
